// Config for the storefront header (NavBar) and footer chrome, shared across pages.
// Persisted as JSON strings on IStorefrontSettings.headerConfig / .footerConfig.
// Mirrors the storefront runtime (ac-storefront-web: Components/Helpers/headerFooterConfig.ts).
// Each section reuses the generic search-config style shape (ISearchElementStyle) and is
// applied with elementStyleFrom — numbers are px, fontSize is a percent, and any camelCase
// CSS passes straight through.

import { ISearchElementStyle } from './searchPageConfig';
import { ILintResult } from './searchPageConfigLint';
import { ILayout } from './layout';
import { IAutocompleteConfig } from './autocompleteConfig';

export interface IHeaderConfig {
    layout?: ILayout;                 // opt-in grid layout for the header (AC-937); absent = fixed header
    sticky?: boolean;                 // pin the header to the top of the viewport while scrolling
    searchRow?: boolean;              // AC-970: render the header search as a full-width row below the header
    searchRowSticky?: boolean;        // AC-970: pin that search row on scroll, independent of header sticky
    searchRowStyle?: ISearchElementStyle; // AC-970: style overrides for the search-row band (bg/parallax, padding, color…)
    hideAutoMenu?: boolean;           // opt-in: suppress the auto "Menu" dropdown fed by storefrontMenuLinks
    brandInline?: boolean;            // opt-in: move the logo+title into the centered header-links group instead of the left-pinned brand slot
    bar?: ISearchElementStyle;        // the header bar itself (<nav>)
    title?: ISearchElementStyle;      // the store-name / title text
    titleText?: string;               // overrides the text beside the logo ('' = logo alone; default = the storefront name)
    logo?: ISearchElementStyle;       // the header logo <img> (default height 3em)
    brand?: ISearchElementStyle;      // the logo+title container (.header-title) that groups them
    searchBar?: ISearchElementStyle;  // the header search input
    searchAutocomplete?: IAutocompleteConfig; // the header bar's suggestion popout: on/off, columns, styling
    menuButton?: ISearchElementStyle; // the "Menu" dropdown trigger button
    menu?: ISearchElementStyle;       // the "Menu" dropdown popover
    menuHeader?: ISearchElementStyle; // the section headers inside the "Menu" dropdown
    menuItem?: ISearchElementStyle;   // the individual entries (links) inside the "Menu" dropdown
    megaPanel?: ISearchElementStyle;  // AC-972: mega-menu panel surface; absent = luminance-aware default
    // ── The account / login persona in the header ────────────────────────────────────────
    // One set of parts covers BOTH states, because to a shopper it is one control that changes
    // label: signed out it reads "Guest / Sign in" and opens the login form; signed in it shows the
    // shopper's name and opens the account menu. Styling them separately would let a storefront drift
    // into looking like two different controls.
    account?: ISearchElementStyle;        // the clickable pill/trigger itself
    accountName?: ISearchElementStyle;    // primary line: the shopper's name, or "Guest"
    accountSubtext?: ISearchElementStyle;  // secondary line: "Sign in" (signed out only)
    accountAvatar?: ISearchElementStyle;  // the round avatar
    accountPanel?: ISearchElementStyle;   // the sign-in popover surface
}

export interface IFooterConfig {
    layout?: ILayout;                 // opt-in grid layout for the footer (AC-937); absent = fixed footer
    bar?: ISearchElementStyle;        // the footer bar itself
    leftText?: ISearchElementStyle;   // the left column (logo + footer text)
    rightText?: ISearchElementStyle;  // the right column (contact / email / phone)
    linkHeader?: ISearchElementStyle; // the footer link section headers (separator entries)
    link?: ISearchElementStyle;       // the regular footer links
    // The social row. Absent socialBadge, the icons are the networks' own full-color brand PNGs;
    // give it a backgroundColor and every icon becomes a uniform glyph on a colored circle, with
    // `color` as the glyph fill and the rest of the object styling the circle.
    socialBadge?: ISearchElementStyle;      // opt-in badge behind each social icon
    socialBadgeHover?: ISearchElementStyle; // hover state of that badge (backgroundColor only); ignored without socialBadge
    socialAlign?: 'left' | 'center' | 'right'; // where the social row sits in the footer (default 'right')
}

export const HEADER_SECTIONS = ['bar', 'title', 'logo', 'brand', 'searchBar', 'menuButton', 'menu', 'menuHeader', 'menuItem', 'megaPanel',
    'account', 'accountName', 'accountSubtext', 'accountAvatar', 'accountPanel'] as const;
export const FOOTER_SECTIONS = ['bar', 'leftText', 'rightText', 'linkHeader', 'link', 'socialBadge', 'socialBadgeHover'] as const;

const parseObj = (raw?: string): Record<string, unknown> => {
    if (!raw) return {};
    try {
        const p = JSON.parse(raw);
        return (p && typeof p === 'object' && !Array.isArray(p)) ? (p as Record<string, unknown>) : {};
    } catch {
        return {};
    }
};

export const parseHeaderConfig = (raw?: string): IHeaderConfig => parseObj(raw) as IHeaderConfig;
export const parseFooterConfig = (raw?: string): IFooterConfig => parseObj(raw) as IFooterConfig;
export const serializeConfig = (config: IHeaderConfig | IFooterConfig): string => JSON.stringify(config);

// ── Linter for the Raw JSON editing mode ───────────────────────────────────────
// Mirrors searchPageConfigLint exactly: every leaf is checked with CSS.supports() so any
// CSS property works but typos are caught. `font`/`fontSize` get the same transform the
// builders apply (fontFamily / percent); every other numeric value becomes a px length.
const APP_KEYS: Record<string, (v: string | number) => { prop: string; val: string }> = {
    font: (v) => ({ prop: 'font-family', val: String(v) }),
    fontSize: (v) => ({ prop: 'font-size', val: `${v}%` }),
};
const DANGEROUS = /(expression\s*\(|javascript\s*:|@import|url\s*\(|<|>)/i;
const kebab = (key: string) => key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
const cssSupports = (prop: string, val: string): boolean => {
    try {
        return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports(prop, val);
    } catch {
        return false;
    }
};

// Validate a config whose top-level keys are all CSS style sections (header/footer).
// `booleanKeys` names top-level flags that are behavioral booleans (e.g. header `sticky`)
// rather than CSS style-section objects, so they're validated as booleans, not warned as unknown.
// `stringKeys` likewise names top-level plain-string values (e.g. header `titleText`), and
// `numberKeys` top-level plain numbers (e.g. the product page's `descriptionCollapsedHeight`).
export function lintStyleSections(raw: string, allowed: readonly string[], booleanKeys: readonly string[] = [], stringKeys: readonly string[] = [], numberKeys: readonly string[] = []): ILintResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${(e as Error).message}`], warnings: [] };
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { valid: false, errors: ['The top-level value must be a JSON object.'], warnings: [] };
    }
    const obj = parsed as Record<string, unknown>;

    for (const key of Object.keys(obj)) {
        if (booleanKeys.includes(key)) {
            if (obj[key] !== undefined && typeof obj[key] !== 'boolean') errors.push(`"${key}" must be a boolean.`);
            continue;
        }
        if (stringKeys.includes(key)) {
            if (obj[key] !== undefined && typeof obj[key] !== 'string') errors.push(`"${key}" must be a string.`);
            else if (typeof obj[key] === 'string' && DANGEROUS.test(obj[key] as string)) errors.push(`"${key}": value not allowed (url(), @import, javascript:, expression() and angle brackets are blocked).`);
            continue;
        }
        if (numberKeys.includes(key)) {
            if (obj[key] !== undefined && typeof obj[key] !== 'number') errors.push(`"${key}" must be a number.`);
            continue;
        }
        if (!allowed.includes(key)) {
            warnings.push(`Unknown key "${key}" — only ${allowed.join(', ')} are read.`);
            continue;
        }
        const style = obj[key];
        if (style === undefined || style === null) continue;
        if (typeof style !== 'object' || Array.isArray(style)) {
            errors.push(`"${key}" must be an object of CSS properties.`);
            continue;
        }
        for (const [prop, value] of Object.entries(style as Record<string, unknown>)) {
            if (value === undefined || value === null) continue;
            if (typeof value !== 'string' && typeof value !== 'number') {
                errors.push(`${key}.${prop} must be a string or number (got ${typeof value}).`);
                continue;
            }
            if (DANGEROUS.test(prop) || DANGEROUS.test(String(value))) {
                errors.push(`${key}.${prop}: value not allowed (url(), @import, javascript:, expression() and angle brackets are blocked).`);
                continue;
            }
            const app = APP_KEYS[prop];
            const cssProp = app ? app(value).prop : kebab(prop);
            const cssVal = app ? app(value).val : (typeof value === 'number' ? `${value}px` : value);
            if (!cssSupports(cssProp, cssVal)) {
                errors.push(`${key}.${prop}: "${cssVal}" is not a valid value for CSS "${cssProp}".`);
            }
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        parsed: errors.length === 0 ? (parsed as import('./searchPageConfig').ISearchPageConfig) : undefined,
    };
}

// The header-links section ({ container, cellStyle, links }) mixes style sections with actual
// link data, so it's validated more loosely than the pure-style header/footer sections:
// container/cellStyle go through the CSS-section validator, links must be an array of objects,
// and the whole section is scanned for disallowed content (it renders to shoppers).
function lintHeaderLinksSection(hl: unknown): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (typeof hl !== 'object' || hl === null || Array.isArray(hl)) {
        errors.push('must be an object.');
        return { errors, warnings };
    }
    if (DANGEROUS.test(JSON.stringify(hl))) {
        errors.push('contains disallowed content (url(), @import, javascript:, or angle brackets).');
        return { errors, warnings };
    }
    const obj = hl as Record<string, unknown>;
    const allowed = ['container', 'cellStyle', 'links'];
    for (const key of Object.keys(obj)) {
        const v = obj[key];
        if (v === undefined || v === null) continue;
        if (!allowed.includes(key)) {
            warnings.push(`Unknown key "${key}" — only container, cellStyle, links are read.`);
            continue;
        }
        if (key === 'links') {
            if (!Array.isArray(v)) { errors.push('links must be an array.'); continue; }
            v.forEach((item, i) => {
                if (typeof item !== 'object' || item === null || Array.isArray(item)) errors.push(`links[${i}] must be an object.`);
            });
        } else {
            const sub = lintStyleSections(JSON.stringify({ [key]: v }), [key]);
            sub.errors.forEach((e) => errors.push(e));
            sub.warnings.forEach((w) => warnings.push(w));
        }
    }
    return { errors, warnings };
}

// Shape checks for the `notifications` section. Deliberately shallow: the style parts on each bar
// are ordinary style dicts already covered by the visual editor, so what's worth catching here is
// the structural damage a hand-edit can do — a bar with no id (unaddressable, so the runtime drops
// it) or two bars sharing one (they'd share a dismissal key, and closing one would close both).
function lintNotificationsSection(n: unknown): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (typeof n !== 'object' || n === null || Array.isArray(n)) {
        return { errors: ['must be an object with a "bars" array.'], warnings };
    }
    const bars = (n as { bars?: unknown }).bars;
    if (bars === undefined) return { errors, warnings };
    if (!Array.isArray(bars)) return { errors: ['"bars" must be an array.'], warnings };

    const DISMISS_MODES = ['none', 'session', 'persistent'];
    const seen = new Set<string>();
    bars.forEach((bar, i) => {
        const at = `bars[${i}]`;
        if (typeof bar !== 'object' || bar === null || Array.isArray(bar)) { errors.push(`${at} must be an object.`); return; }
        const b = bar as Record<string, unknown>;
        if (typeof b.id !== 'string' || !b.id) errors.push(`${at}.id is required — a bar without one is dropped.`);
        else if (seen.has(b.id)) errors.push(`${at}.id "${b.id}" is already used — bars sharing an id share a dismissal.`);
        else seen.add(b.id);

        if (b.dismissMode !== undefined && !DISMISS_MODES.includes(b.dismissMode as string)) {
            errors.push(`${at}.dismissMode must be one of ${DISMISS_MODES.join(', ')}.`);
        }
        const start = typeof b.startsAt === 'string' ? Date.parse(b.startsAt) : undefined;
        const end = typeof b.endsAt === 'string' ? Date.parse(b.endsAt) : undefined;
        if (b.startsAt !== undefined && (typeof b.startsAt !== 'string' || Number.isNaN(start))) {
            errors.push(`${at}.startsAt must be an ISO date/time string.`);
        }
        if (b.endsAt !== undefined && (typeof b.endsAt !== 'string' || Number.isNaN(end))) {
            errors.push(`${at}.endsAt must be an ISO date/time string.`);
        }
        if (start !== undefined && end !== undefined && !Number.isNaN(start) && !Number.isNaN(end) && end <= start) {
            warnings.push(`${at} ends at or before it starts, so it will never show.`);
        }
        if (!b.text && !b.linkText) warnings.push(`${at} has no message, so it will render as an empty bar.`);
    });
    return { errors, warnings };
}

// Combined linter for the single advanced editor on the Header & Footer page. The header, footer,
// header-links and notifications configs are stored as separate fields, but the merchant edits them
// together as one object `{ header, footer, headerLinks, notifications }` — so there's exactly one
// Raw JSON control on the page. header/footer are validated as style sections (header also allows
// the `sticky` boolean); headerLinks and notifications get the looser shape checks above/below.
export function lintChromeConfig(raw: string): ILintResult {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${(e as Error).message}`], warnings: [] };
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { valid: false, errors: ['The top-level value must be a JSON object with "header", "footer", "headerLinks" and/or "notifications" keys.'], warnings: [] };
    }
    const obj = parsed as Record<string, unknown>;
    const errors: string[] = [];
    const warnings: string[] = [];
    for (const key of Object.keys(obj)) {
        const section = obj[key];
        if (section === undefined || section === null) continue;
        if (key === 'header' || key === 'footer') {
            if (typeof section !== 'object' || Array.isArray(section)) { errors.push(`"${key}" must be an object.`); continue; }
            const sub = key === 'header'
                ? lintStyleSections(JSON.stringify(section), HEADER_SECTIONS, ['sticky', 'hideAutoMenu', 'brandInline'], ['titleText'])
                : lintStyleSections(JSON.stringify(section), FOOTER_SECTIONS, [], ['socialAlign']);
            sub.errors.forEach((e) => errors.push(`${key}.${e}`));
            sub.warnings.forEach((w) => warnings.push(`${key} → ${w}`));
        } else if (key === 'headerLinks') {
            const r = lintHeaderLinksSection(section);
            r.errors.forEach((e) => errors.push(`headerLinks.${e}`));
            r.warnings.forEach((w) => warnings.push(`headerLinks → ${w}`));
        } else if (key === 'notifications') {
            const r = lintNotificationsSection(section);
            r.errors.forEach((e) => errors.push(`notifications.${e}`));
            r.warnings.forEach((w) => warnings.push(`notifications → ${w}`));
        } else {
            warnings.push(`Unknown key "${key}" — only header, footer, headerLinks, notifications are read.`);
        }
    }
    return { valid: errors.length === 0, errors, warnings };
}
